import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";
import { Roles, RouteType } from "../models/enum";
import { RoleService } from "../shared/services/role.service";
import { GoogleAuthService } from "./google-api.service";

const ACTION_CENTER_ROLE_PRIORITY: Roles[] = [
	Roles.INSTRUCTOR,
	Roles.CABIN_CREW_INSTRUCTOR,
	Roles.PILOT,
	Roles.CABIN_CREW,
];

export function resolveActionCenterRole(roleService: RoleService): Roles | null {
	const selectedRole = roleService.userDetails?.selectedRole as Roles | undefined;
	if (
		selectedRole === Roles.ADMIN ||
		selectedRole === Roles.GROUP_ADMIN ||
		selectedRole === Roles.OFFICE_ADMIN
	) {
		return null;
	}

	if (selectedRole && ACTION_CENTER_ROLE_PRIORITY.includes(selectedRole)) {
		return selectedRole;
	}

	if (roleService.isInstructor) return Roles.INSTRUCTOR;
	if (roleService.isCabinCrewInstructor) return Roles.CABIN_CREW_INSTRUCTOR;
	if (roleService.isPilot) return Roles.PILOT;
	if (roleService.isCabinCrew) return Roles.CABIN_CREW;

	const roles = roleService.userDetails?.roles ?? [];
	return ACTION_CENTER_ROLE_PRIORITY.find((role) => roles.includes(role)) ?? null;
}

@Injectable({
	providedIn: "root",
})
export class ActionCenterGuard {
	constructor(
		private router: Router,
		private roleService: RoleService,
		private authService: GoogleAuthService,
	) {}

	async canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
		const allow = (): boolean => {
			const actionCenterRole = resolveActionCenterRole(this.roleService);
			if (!actionCenterRole) {
				return false;
			}

			const selectedRole = this.roleService.userDetails?.selectedRole;
			if (selectedRole === actionCenterRole) {
				return true;
			}

			const path = state.url.split("?")[0];
			if (!path.startsWith("/checklist") && !path.startsWith("/my-feedback")) {
				return false;
			}

			this.roleService.autoSwitchRole(actionCenterRole);
			this.roleService.setRolesBool();
			return true;
		};

		if (!navigator.onLine) {
			if (this.authService.hasValidOfflineSession() && allow()) {
				return true;
			}
			this.router.navigate([this.roleService.getDefaultRoute?.() ?? RouteType.DASHBOARD]);
			return false;
		}

		const authenticated = await this.authService.ensureSessionResolved();
		if (authenticated && allow()) {
			return true;
		}

		this.router.navigate([this.roleService.getDefaultRoute?.() ?? RouteType.DASHBOARD]);
		return false;
	}
}
